import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { STATIC_BASE, formatApiError } from "@/lib/api";
import {
  ArrowLeft, FileText, Music, Plus, X, ShoppingCart, CreditCard,
  Clock, CheckCircle2, Sparkles,
} from "lucide-react";

const STATUS_META = {
  brief_draft:       { label: "Brief in Progress", color: "text-white/70 border-white/20 bg-white/5" },
  lyrics_pending:    { label: "Lyrics Pending",    color: "text-blue-300 border-blue-400/40 bg-blue-400/5" },
  lyrics_review:     { label: "Lyrics Review",     color: "text-gold border-gold/40 bg-gold/5" },
  lyrics_submitted:  { label: "In Music Production", color: "text-blue-300 border-blue-400/40 bg-blue-400/5" },
  variations_ready:  { label: "Songs Ready for Review", color: "text-gold border-gold/40 bg-gold/5 animate-pulse" },
  completed:         { label: "Completed", color: "text-green-400 border-green-400/40 bg-green-400/5" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [addons, setAddons] = useState([]);
  const [totals, setTotals] = useState({ total: 0, breakdown: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a, t] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/addons`),
        api.get(`/projects/${id}/cart-totals`).catch(() => ({ data: { total: 0, breakdown: [] } })),
      ]);
      setProject(p.data);
      setAddons(a.data);
      setTotals(t.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addItem = async (payload) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/projects/${id}/cart`, payload);
      setTotals(data.totals);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (itemId) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/projects/${id}/cart/${itemId}`);
      setTotals(data.totals);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const checkout = async () => {
    setCheckoutBusy(true); setError("");
    try {
      const { data } = await api.post(`/projects/checkout`, {
        project_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setCheckoutBusy(false);
    }
  };

  const download = async (variationId, title) => {
    try {
      const res = await api.get(`/orders/${id}/variations/${variationId}/download`, { responseType: "blob" });
      // Fallback: variations endpoint is on orders for legacy. Try projects path:
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      // Direct file URL fallback (since variations have audio_url)
      const v = project.variations.find((x) => x.id === variationId);
      if (v) window.open(`${STATIC_BASE}${v.audio_url}`, "_blank");
    }
  };

  if (!project) return <div className="container-x py-20 text-white/60">{error || "Loading…"}</div>;

  const status = STATUS_META[project.status] || { label: project.status, color: "text-white/70 border-white/20 bg-white/5" };
  const bp = project.brief?.business_profile || {};
  const inCart = (vid) => (project.cart_items || []).some((i) => i.variation_id === vid);
  const addonInCart = (k) => (project.cart_items || []).some((i) => i.addon_key === k);
  const purchasedVid = (vid) => (project.purchased_items || []).some((i) => i.variation_id === vid);
  const songsInCart = (project.cart_items || []).filter((i) => i.type === "song").length;

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-10 max-w-5xl">
        <Link to="/dashboard" className="text-white/60 hover:text-gold text-sm inline-flex items-center gap-2 mb-6" data-testid="back-dashboard">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="bg-ink-900 border border-ink-700 p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-1">Project</div>
              <h1 className="font-heading text-2xl md:text-3xl">{bp.business_name || "Untitled Brief"}</h1>
              <div className="text-white/50 text-sm mt-1">Created {new Date(project.created_at).toLocaleDateString()}</div>
            </div>
            <div className={`text-[10px] tracking-[0.22em] uppercase font-semibold border px-3 py-1.5 ${status.color}`}>{status.label}</div>
          </div>
        </div>

        {/* Status-aware content */}
        {project.status === "brief_draft" && (
          <ActionCard
            icon={FileText}
            title="Finish your business brief"
            body="You started a brief but haven't completed it. Resume where you left off — we save your progress automatically."
            actionLabel="Continue Brief"
            onAction={() => nav(`/dashboard/projects/${id}/brief`)}
            testid="resume-brief"
          />
        )}

        {project.status === "lyrics_review" && (
          <ActionCard
            icon={Sparkles}
            title="Your lyrics are ready to review"
            body="Open the lyrics editor to fine-tune and submit them for music production."
            actionLabel="Open Lyrics"
            onAction={() => nav(`/dashboard/projects/${id}/lyrics`)}
            testid="open-lyrics"
          />
        )}

        {project.status === "lyrics_submitted" && (
          <div className="bg-ink-900 border border-ink-700 p-10 text-center" data-testid="awaiting-production">
            <Clock className="text-gold mx-auto mb-4" size={42} />
            <h3 className="font-heading text-2xl mb-2">In Music Production</h3>
            <p className="text-white/65 max-w-md mx-auto mb-2">
              Perry has your lyrics and is producing your 3 song versions. You&apos;ll be notified when songs are ready to preview and purchase.
            </p>
            <p className="text-white/45 text-xs">Submitted {project.lyrics_submitted_at && new Date(project.lyrics_submitted_at).toLocaleString()}</p>
          </div>
        )}

        {(project.status === "variations_ready" || project.status === "completed") && (
          <>
            {/* Lyrics */}
            <details className="bg-ink-900 border border-ink-700 mb-6">
              <summary className="cursor-pointer p-5 text-sm tracking-[0.18em] uppercase text-gold font-semibold flex items-center gap-2">
                <FileText size={14} /> Submitted Lyrics
              </summary>
              <pre className="px-5 pb-5 whitespace-pre-wrap font-mono text-white/85 text-sm">{project.lyrics_final}</pre>
            </details>

            {/* Variations */}
            <h2 className="font-heading text-2xl mb-4">Your Song Versions</h2>
            {(!project.variations || project.variations.length === 0) ? (
              <div className="text-white/50">No variations uploaded yet.</div>
            ) : (
              <div className="space-y-4 mb-8">
                {project.variations.map((v) => {
                  const purchased = purchasedVid(v.id);
                  const selected = inCart(v.id);
                  let borderClass = "border-ink-700";
                  if (purchased) borderClass = "border-green-500/40";
                  else if (selected) borderClass = "border-gold";
                  return (
                    <div key={v.id} className={`bg-ink-900 border p-5 ${borderClass}`} data-testid={`variation-${v.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                        <div>
                          <h3 className="font-heading text-lg">{v.title}</h3>
                          {v.description && <p className="text-white/60 text-sm mt-1">{v.description}</p>}
                        </div>
                        <div>
                          {purchased ? (
                            <span className="inline-flex items-center gap-1 text-green-400 text-xs font-semibold uppercase tracking-widest">
                              <CheckCircle2 size={14} /> Purchased
                            </span>
                          ) : (
                            <span className="text-gold text-xs font-semibold uppercase tracking-widest">Preview Only</span>
                          )}
                        </div>
                      </div>
                      <audio controls preload="none" src={`${STATIC_BASE}${v.audio_url}`} data-testid={`var-audio-${v.id}`} />
                      <div className="mt-3 flex flex-wrap gap-3">
                        {purchased ? (
                          <button onClick={() => download(v.id, v.title)} className="btn-outline" data-testid={`download-${v.id}`}>Download</button>
                        ) : selected ? (
                          <button
                            onClick={() => removeItem(project.cart_items.find((i) => i.variation_id === v.id)?.id)}
                            className="btn-gold" disabled={busy}
                            data-testid={`unselect-${v.id}`}
                          >
                            <X size={14} /> Remove from Cart
                          </button>
                        ) : (
                          <button
                            onClick={() => addItem({ type: "song", variation_id: v.id })}
                            className="btn-outline" disabled={busy || songsInCart >= 3}
                            data-testid={`select-${v.id}`}
                          >
                            <Plus size={14} /> Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add-ons */}
            {addons.length > 0 && project.status === "variations_ready" && (
              <>
                <h3 className="font-heading text-xl mb-3">Optional Add-Ons</h3>
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {addons.map((a) => {
                    const sel = addonInCart(a.key);
                    return (
                      <div key={a.id} className={`bg-ink-900 border p-4 transition ${sel ? "border-gold" : "border-ink-700"}`} data-testid={`addon-${a.key}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-heading text-base">{a.name}</div>
                            <div className="text-white/55 text-xs mt-1">{a.description}</div>
                          </div>
                          <div className="text-gold font-semibold">${a.price.toFixed(0)}</div>
                        </div>
                        <div className="mt-3">
                          {sel ? (
                            <button
                              onClick={() => removeItem(project.cart_items.find((i) => i.addon_key === a.key)?.id)}
                              className="btn-gold !py-2 !px-4 !text-xs" data-testid={`addon-remove-${a.key}`}
                            >
                              <X size={12} /> Remove
                            </button>
                          ) : (
                            <button
                              onClick={() => addItem({ type: "addon", addon_key: a.key })}
                              className="btn-outline !py-2 !px-4 !text-xs" disabled={busy}
                              data-testid={`addon-add-${a.key}`}
                            >
                              <Plus size={12} /> Add
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Cart */}
            {project.cart_items && project.cart_items.length > 0 && (
              <div className="bg-ink-900 border-2 border-gold p-6 sticky bottom-4" data-testid="cart-section">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingCart className="text-gold" size={20} />
                  <h3 className="font-heading text-xl">Shopping Cart</h3>
                </div>
                <div className="space-y-2 mb-4">
                  {totals.breakdown?.map((b, i) => (
                    <div key={i} className="flex justify-between text-white/85 text-sm">
                      <span>{b.label}</span>
                      <span className="text-gold font-semibold">${b.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-ink-700 pt-3 flex items-baseline justify-between mb-5">
                  <span className="eyebrow">Grand Total</span>
                  <span className="font-heading text-3xl text-gold">${totals.total?.toFixed(2)}</span>
                </div>
                {error && <div className="text-destructive text-sm mb-3">{error}</div>}
                <button onClick={checkout} disabled={checkoutBusy} className="btn-gold w-full justify-center" data-testid="checkout-button">
                  <CreditCard size={16} /> {checkoutBusy ? "Redirecting…" : "Check Out & Unlock Songs"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, body, actionLabel, onAction, testid }) {
  return (
    <div className="bg-ink-900 border border-gold/40 p-8 text-center" data-testid={testid}>
      <Icon className="text-gold mx-auto mb-4" size={42} />
      <h3 className="font-heading text-2xl mb-2">{title}</h3>
      <p className="text-white/65 max-w-md mx-auto mb-6">{body}</p>
      <button onClick={onAction} className="btn-gold">{actionLabel}</button>
    </div>
  );
}
